import React, { useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getCachedPageTitle } from '@confluence/editor-features/entry-points/utils/getCachedPageTitle.ts';

import { createRemoteLink } from './createRemoteLink.ts';

const i18n = defineMessages({
	flagTitle: {
		id: 'experiment-jira-smart-links.flag.success.title',
		defaultMessage: 'Linked to your Jira work item',
		description:
			'Title text for a flag saying the page they are viewing was successfully linked to a Jira work item',
	},
	flagDescription: {
		id: 'experiment-jira-smart-links.flag.success.description',
		defaultMessage: 'Your live doc was created and linked to Jira work item {issueKey}',
		description:
			'Description text for a flag saying the page they are viewing was successfully linked to a Jira work item',
	},
});

// Jira 3P Smart link experiment query params
const REMOTE_ISSUE_LINK_PARAM = 'remoteIssueKey';
const getRemoteIssueKey = createSingleQueryParamHook(REMOTE_ISSUE_LINK_PARAM);

type Props = {
	contentId: string;
};

/**
 * While useFlags from atlaskit is more preferable, the FlagsProviderContext is not available
 * in the render path for LivePageComponent.
 */
export const RemoteLinkFlag: React.ComponentType<Omit<Props, 'flags'>> = withFlags(
	({ flags, contentId }: WithFlagsProps & Props) => {
		const intl = useIntl();
		const { cloudId } = useSessionData();
		const remoteIssueKey = getRemoteIssueKey();
		const { title } = getCachedPageTitle(contentId);
		const { setQueryParams } = useRouteActions();

		useEffect(() => {
			if (remoteIssueKey && expValEquals('cc_fd_jira_issue_live_doc_prompt', 'isEnabled', true)) {
				const connectPageToIssue = async () => {
					const res = await createRemoteLink({
						cloudId,
						issueKey: remoteIssueKey,
						url: window.location.href,
						title: title || 'Confluence Live Doc',
					});

					if (res) {
						void flags.showCustomFlag({
							id: 'experiment-jira-smart-links.success.flag',
							title: intl.formatMessage(i18n.flagTitle),
							description: intl.formatMessage(i18n.flagDescription, {
								issueKey: remoteIssueKey,
							}),
							customIcon: (
								<StatusSuccessIcon
									color={token('color.icon.accent.green')}
									label=""
									spacing="spacious"
								/>
							),
							isAutoDismiss: false,
						});

						setQueryParams(
							{
								[REMOTE_ISSUE_LINK_PARAM]: null,
							},
							true,
						);
					}
				};

				void connectPageToIssue();
			}
		}, [remoteIssueKey, cloudId, flags, intl, title, setQueryParams]);

		return <></>;
	},
);
